import { useCallback, useMemo, useRef, useState } from 'react';
import { Icon } from '../../../../core/components/atoms/Icon';
import { Typography } from '../../../../core/components/molecules/Typography';
import useUserMemory from '../../hooks/use-user-memory';
import { flashcardService } from '../../queries';
import { CardWrapper, IconRemove } from './variants';
import { Colors } from '../../../../core/models/disciplines';
import { TAG_TYPE } from '../../hooks/use-tags';
import QuickFlashcardReview from '../quick-flashcard-review';
import Tag from '../tag';
import MemoryModalAction from '../modal-action';
import { Button } from '../../../../core/components/atoms/Button';

const CardDiscipline = ({
  front,
  uuid,
  tags,
  onSuccessFallback,
  discipline,
  hasVinculed,
  className,
  shouldReview,
  subject,
}: {
  front: string;
  uuid: string;
  tags?: Tag[];
  discipline?: Tag;
  onSuccessFallback?: VoidFunction;
  hasVinculed?: boolean;
  className?: string;
  classNameIcon?: string;
  shouldReview?: boolean;
  subject?: {
    id: string;
    name: string;
  };
}) => {
  const [isOpenQuickFlashcardReview, setIsOpenQuickFlashcardReview] =
    useState(false);

  const [isOpenModalProcessReview, setIsOpenModalProcessReview] =
    useState(false);

  const [isOpenModalDeleteFlashcard, setIsOpenModalDeleteFlashcard] =
    useState(false);

  const [
    isOpenModalProcessReviewCanceled,
    setIsOpenModalProcessReviewCanceled,
  ] = useState(false);

  const { memoryUserUuid } = useUserMemory();

  const scrollRef = useRef<HTMLDivElement>(null);
  const touchStartY = useRef(0);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    const el = scrollRef.current;
    if (!el) return;

    const deltaY = touchStartY.current - e.touches[0].clientY;
    touchStartY.current = e.touches[0].clientY;

    const isScrollable = el.scrollHeight > el.clientHeight;
    if (!isScrollable) return;

    const isAtTop = el.scrollTop <= 0 && deltaY < 0;
    const isAtBottom =
      el.scrollTop + el.clientHeight >= el.scrollHeight && deltaY > 0;

    if (!isAtTop && !isAtBottom) {
      e.stopPropagation();
    }

    el.scrollTop += deltaY;
  }, []);

  const removeFromDeck = async () => {
    if (!memoryUserUuid) return;
    await flashcardService.removeFlashcardFromDeck(memoryUserUuid, uuid);
    onSuccessFallback?.();

    setIsOpenModalDeleteFlashcard(false);
    setIsOpenModalProcessReviewCanceled(true);
  };

  const disciplineTag = useMemo(() => {
    const disciplineTag = tags?.find((tag) => tag.type === TAG_TYPE.DISCIPLINA);

    return disciplineTag || discipline;
  }, [discipline, tags]);

  const handleCloseModalFlashcardReview = () => {
    setIsOpenModalProcessReview(false);
    setIsOpenQuickFlashcardReview(false);
    setIsOpenModalDeleteFlashcard(false);
  };

  const onActionModal = useCallback(() => {
    return hasVinculed && !shouldReview
      ? setIsOpenModalProcessReview(true)
      : setIsOpenQuickFlashcardReview(true);
  }, [hasVinculed, shouldReview]);

  const handleModalDeleteFlashcard = () => {
    setIsOpenModalProcessReview(false);
    setIsOpenModalDeleteFlashcard(true);
  };

  const flashcardActiveToReview = useMemo(() => {
    return !shouldReview;
  }, [shouldReview]);

  return (
    <div
      className={CardWrapper({
        color: disciplineTag?.slug as Colors,
        className,
        selected: flashcardActiveToReview && hasVinculed,
      })}
      onClick={onActionModal}
    >
      <div className='mb-4 flex flex-row justify-between gap-2'>
        {disciplineTag && (
          <Tag
            key={disciplineTag.uuid}
            text={disciplineTag.name}
            disabled={!shouldReview && hasVinculed}
            color={disciplineTag?.slug as Colors}
          />
        )}
        {hasVinculed && (
          <Tag
            text='Processo de Revisão'
            disabled={true}
            hasVinculed={hasVinculed}
            type='review'
          />
        )}
        {hasVinculed && (
          <button
            className='absolute right-1 top-2 flex h-4 w-4'
            aria-label='Remover'
            onClick={() => setIsOpenModalDeleteFlashcard(true)}
          >
            <Icon
              Icon='do_not_disturb_on'
              type='material'
              className={IconRemove({ color: disciplineTag?.slug as Colors })}
            />
          </button>
        )}
      </div>
      <div
        ref={scrollRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        className='[&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent max-h-[100px] overflow-y-scroll [touch-action:pan-y] [&::-webkit-scrollbar-thumb]:bg-[#d1d5dc] [&::-webkit-scrollbar]:w-1'
      >
        <Typography
          type='title'
          size='h4'
          className={`leading-snug ${!shouldReview && hasVinculed ? 'text-mono-color-dark' : 'text-card-color-default'}`}
        >
          {front}
        </Typography>
      </div>

      <MemoryModalAction
        isOpen={isOpenModalDeleteFlashcard}
        onClose={() => setIsOpenModalDeleteFlashcard(false)}
        title={
          <i className='font-bold text-[#2E3847] dark:text-[#FFF]'>Flashcard</i>
        }
        description={
          <>
            Tem certeza que quer cancelar o Processo de Revisão deste{' '}
            <i>Flashcard</i>?
          </>
        }
      >
        <MemoryModalAction.Footer className='flex flex-row gap-2'>
          <Button
            className='w-full justify-center text-center'
            variant='outlined'
            onClick={() => setIsOpenModalDeleteFlashcard(false)}
          >
            <Typography>Não</Typography>
          </Button>
          <Button
            className='w-full justify-center text-center'
            variant='contained'
            onClick={removeFromDeck}
          >
            <Typography className='text-[#FFF]'>
              Sim, Cancelar Revisão
            </Typography>
          </Button>
        </MemoryModalAction.Footer>
      </MemoryModalAction>

      <MemoryModalAction
        isOpen={isOpenModalProcessReviewCanceled}
        onClose={() => setIsOpenModalProcessReviewCanceled(false)}
        title={
          <i className='font-bold text-[#2E3847] dark:text-[#FFF]'>Flashcard</i>
        }
        description='Processo de Revisão Cancelado'
      >
        <MemoryModalAction.Footer className='flex flex-row gap-2'>
          <Button
            className='w-full justify-center text-center'
            variant='contained'
            onClick={() => setIsOpenModalProcessReviewCanceled(false)}
          >
            <Typography className='text-[#FFF]'>Fechar</Typography>
          </Button>
        </MemoryModalAction.Footer>
      </MemoryModalAction>

      <MemoryModalAction
        isOpen={isOpenModalProcessReview}
        onClose={() => setIsOpenModalProcessReview(false)}
        title={<i>Flashcard</i>}
      >
        <MemoryModalAction.Body>
          <Typography className='font-bold'>
            Este <i>flashcard</i> está em Processo de Revisão.
          </Typography>
          <Typography>
            Você poderá acessá-lo novamente quando o sistema indicar o melhor
            momento.
          </Typography>
        </MemoryModalAction.Body>
        <MemoryModalAction.Footer className='flex flex-row gap-2'>
          <Button
            className='w-full justify-center text-center'
            variant='outlined'
            onClick={handleModalDeleteFlashcard}
          >
            <Typography>Cancelar Processo de Revisão</Typography>
          </Button>
          <Button
            className='w-full justify-center text-center'
            variant='contained'
            onClick={() => handleCloseModalFlashcardReview()}
          >
            <Typography className='text-[#FFF]'>Fechar</Typography>
          </Button>
        </MemoryModalAction.Footer>
      </MemoryModalAction>

      <QuickFlashcardReview
        flashcardUuid={uuid}
        isOpen={isOpenQuickFlashcardReview}
        onClose={handleCloseModalFlashcardReview}
        onSuccessFallback={onSuccessFallback}
        subject={subject}
      />
    </div>
  );
};

export default CardDiscipline;
